
/**
 * UX for the the ZMessage which is a max-msp inspired object
 * providing a generic interface for passing messages to other
 * objects
 */

import ZObjectPositioning from './ZObjectPositioning.js';
import React, { useRef, useEffect, useCallback, useState} from 'react';
import {
    setZObjectContextMenu,
    setSelectedZObject,
    setSelectedPulledElements} from 'actions/index.js';
import SubPatchInlet from '../subpatch/SubPatchInlet.js';
import {connect} from 'react-redux';
import {NUMBER, BANG} from 'zobject/data-types.js';
import {useWindowManager} from "components/windows/useWindowManager.js";
import {useDoubleClick} from "components/hooks/useDoubleClick.js";
import MessageInlets from "components/messaging/MessageInlets.js";
import MessageOutlets from "components/messaging/MessageOutlets.js";
import ZNumber from './ZNumber.js';

import {doc} from 'zobject/definitions/docs.js';

import '../../zobject.scss';

const ZMessageComponent = ({
    zmessage,
    messageText,
    coords,
    sequencerEngine,
    scale,
    customMode,
    sizeRef,
    containerRef,
    elements,
    setOrdering,
    isSelected,
    isSelectedZObject,
    ordering,
    size,
    presentationMode,
    isInstrumentView,
    hidden,
    includeInPresentation,
    setSize,
    setSelectedZObject,
    uxLocked,
    isDeleted,
    setZObjectContextMenu
}) => { 
    
    const ref = useRef(undefined);
    const inputRef = useRef(undefined);

    let [text, setText] = useState(messageText);
    let [editing, setEditing] = useState(false);

    const _onDoubleClick = useCallback((e) => {
        e.stopPropagation();
        if (!uxLocked) {
            setEditing(true);
        }
    }, [uxLocked ,setEditing]);
    
    const onClick = useCallback((e) => {
        e.stopPropagation();
        if (uxLocked) {
            zmessage.execute();
        } 

        setSelectedZObject(zmessage);
    }, [uxLocked, zmessage, setEditing]);

    const onDoubleClick = useDoubleClick(_onDoubleClick, 250, onClick);

    useEffect(() => {
        setText(messageText);
    }, [setText, messageText]);
    
    useEffect(() => {
        if (editing && !uxLocked && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editing, uxLocked]);

    useEffect(() => {
        if (uxLocked) {
            setEditing(false);
        }
    }, [uxLocked, setEditing]);

    const onTextChange = useCallback((e) => {
        let _text = e.target.value;
        zmessage.text = _text;
        setText(_text);
    }, [setText]);


    const {style, isDragging, onMouseDown, onMouseUp, setCoords} = useWindowManager({
        scale,
        ref,
        sequencerEngine,
        sizeRef,
        containerRef,
        size,
        setSize,
        elements: elements,
        element: zmessage,
        setOrdering,
        ordering,
    });

    const onContextMenu = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        let rect = containerRef.current.getBoundingClientRect();
        let x = e.clientX - rect.left;
        let y = e.clientY - rect.top;
        setZObjectContextMenu(zmessage.subSequence.id, {x,y}, zmessage);
    }, [zmessage]);

    let _style = {... style};

    const execute = useCallback(() => {
        zmessage.execute();
    }, [zmessage]);

    const onKeyDown = useCallback((e) => {
        if (e.key === "Enter") {
            zmessage.text = text;
            zmessage.execute();
            setEditing(false);
        }
    }, [setEditing, text]);


    let textStyle = {};
    if (text && text.length > 0) {
        if (!zmessage.isOutlet && !zmessage.isInlet) {
            textStyle.width = (text.length + 2) + "ch";
        }
    }

    _style = {
        ... _style,
        ... textStyle
    };

    let className = "z-object z-message";
    if (isSelected ) {
        className += " selected";
    }

    if (isSelectedZObject) {
        className += " selected-object";
    }

    if (zmessage.isOutlet || zmessage.isInlet) {
        className += " io";
    }

    if (includeInPresentation) {
        className += " include-in-presentation";
    }
    if (zmessage.type === BANG) {
        className += " bang";
    }
    if (zmessage.type === NUMBER) {
        className += " number";
    }

    if (uxLocked) {
        className += " locked";
    }

    let windows = zmessage.state.windows;
    let position = presentationMode ? windows.presentationWindowPositions[zmessage.id] ||
        windows.windowPositions[zmessage.id] : windows.windowPositions[zmessage.id];
 
    if (customMode === "custom") {
        let [w,h] = [zmessage.subSequence.zObject.width, zmessage.subSequence.zObject.height];
        let coords = position;
        if (coords && (coords.x > w || coords.y > h)) {
            return '';
        }
    }

    if (isDeleted || hidden) {
        return '';
    }

    let attributes = zmessage.attributes;
    
    let buttonStyling = {};
    let messageStyling = {};
    if (attributes) {
        _style["backgroundColor"] = attributes["background-color"];
        messageStyling["color"] = attributes["primary-color"];
        buttonStyling["borderColor"] = attributes["primary-color"];
        buttonStyling["backgroundColor"] = attributes["secondary-color"];
        if (attributes["width"]) {
            _style["width"] = attributes["width"]+"px";
            messageStyling["width"] = attributes["width"]+"px";
        }
        if (attributes["align to center"]) {
            messageStyling["text-align"] = "center";
        }
    }

    let positions = zmessage.sequencerEngine.state.windows.windowPositions;
    if (positions[zmessage.id]) {
        _style.left = positions[zmessage.id].x;
        _style.top = positions[zmessage.id].y;
    }
    return (<div
              onClick={(e) => {
                  e.stopPropagation();
              }}
              style={messageStyling}
              onMouseDown={customMode === "custom" ? () => 0 : onMouseDown}
              onContextMenu={onContextMenu}
              onMouseUp={onMouseUp}
              ref={ref}
              style={_style} className={className}>
              <ZObjectPositioning isInstrumentView={isInstrumentView} zobject={zmessage} windowRef={ref}/>
              {
                  zmessage.type === NUMBER ?
                      <ZNumber color={attributes && attributes["primary-color"]} isSelectedZObject={isSelectedZObject} number={messageText} znumber={zmessage}/> :
                  zmessage.type === BANG ?
                      <div

                        style={buttonStyling}
                        onClick={() => {
                            setSelectedZObject(zmessage);
                            if (uxLocked) {
                                zmessage.execute();
                            }
                        }}
                        className="bang-button"/> :
                  zmessage.isInlet ?
                      <SubPatchInlet zmessage={zmessage}/>
                  :
                  zmessage.isOutlet ?
                      <SubPatchInlet field="outletNumber" icon="outlet-icon" zmessage={zmessage}/>
                  :
                  !editing || uxLocked ? <div style={messageStyling} onClick={onDoubleClick} className="message-content">{text}</div>
                  :
                  <input
                    spellcheck="false"
                    style={textStyle}
                    onKeyDown={onKeyDown}
                    ref={inputRef}
                    onBlur={() => setEditing(false)}
                    type="text"
                    className="z-object-input"
                    onChange={onTextChange}
                    value={text}/>}
              {!zmessage.isInlet &&
               <MessageInlets
                 numInlets={zmessage.numInlets}
                 effect={zmessage}/>}
              {!zmessage.isOutlet &&
               <MessageOutlets
                 numOutlets={zmessage.numOutlets}
                 subSequence={zmessage.subSequence}
                 containerRef={containerRef}
                 scale={scale}
                 effect={zmessage}/>}
            </div>);
};

const mapStateToProps = (state, props) => {
    let presentationMode = state.spatial.presentationMode || props.isInstrumentView;
    let hidden = false;
    if (presentationMode && props.zmessage.attributes && !props.zmessage.attributes["Include in Presentation"]) {
        hidden = true;
    }
  return {
      presentationMode,
      hidden,
      isSelected: state.windows.selectedElements[props.zmessage.id],
      isSelectedZObject: state.zobject.selectedZObject === props.zmessage,
      messageText: props.zmessage._text,
      uxLocked: state.zobject.uxLocked,
      isDeleted: props.zmessage.isDeleted,
      styling: state.zobject.objectStylings[props.zmessage.id],
      includeInPresentation: !presentationMode && props.zmessage.attributes["Include in Presentation"],
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
      setSelectedZObject : x => dispatch(setSelectedZObject(x)),
      setZObjectContextMenu: (id, position, zobject) => dispatch(setZObjectContextMenu(id, position, zobject)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ZMessageComponent);
